import { shouldRememberReturnPath } from './takeover-paths';

/**
 * Remembers the last work location (any non-takeover path) so every takeover
 * surface's Back affordance (and `esc`) restores exactly where the user was
 * working. Configure, Inbox, and Automations share one memory on purpose:
 * `esc` always exits the takeover layer back to work, never into another
 * takeover. Session-scoped on purpose: a fresh tab starting inside a
 * takeover falls back to the org's sessions page instead of a stale
 * location from another visit.
 */
const STORAGE_KEY = 'takeover-return-to';

export function rememberTakeoverReturnPath(path: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    /* storage unavailable (private mode, quota) — Back falls back to runs */
  }
}

export function readTakeoverReturnPath(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * The remembered work location, or null when storage is empty / holds a
 * path that is itself a takeover. Personal `/settings` used to be a work
 * location before the Account takeover; a stale entry would make Back a
 * no-op that re-enters Account. Callers pass the same labs/account extras
 * {@link shouldRememberReturnPath} uses when writing.
 */
export function readTakeoverWorkPath(
  labsExtra?: ReadonlySet<string>,
  accountExtra?: ReadonlySet<string>,
): string | null {
  const remembered = readTakeoverReturnPath();
  if (!remembered) return null;
  if (!shouldRememberReturnPath(remembered, labsExtra, accountExtra)) return null;
  return remembered;
}
